import React, { useState, useEffect, useCallback } from 'react';
import { applicants } from '../api';
import ApplicantNotes from './ApplicantNotes';
import ApplicantResume from './ApplicantResume';
import { COUNTRY_OPTIONS, DEFAULT_PHONE_COUNTRY, guessPhoneCountry, isValidPhoneForCountry } from '../utils/phone';


function ActiveList({ user }) {
  const [applicantsList, setApplicantsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState(''); // What user types
  const [activeSearch, setActiveSearch] = useState(''); // What actually gets searched
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [viewingApplicant, setViewingApplicant] = useState(null);
  const [editingContact, setEditingContact] = useState(false);
  const [contactInfo, setContactInfo] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    phone_country: DEFAULT_PHONE_COUNTRY,
    address: '',
    city: '',
    state: '',
    zip: ''
  });
  const [contactPhoneError, setContactPhoneError] = useState('');

  const [sortField, setSortField] = useState('applied_date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0 });
  const [filterOptions, setFilterOptions] = useState({ positions: [], locations: [] });
  const [filters, setFilters] = useState({
    position: '',
    location: '',
    viewed: '',
    interviewed: '',
    minRating: '',
    maxRating: ''
  });
  const [tempFilters, setTempFilters] = useState({
    position: '',
    location: '',
    viewed: '',
    interviewed: '',
    minRating: '',
    maxRating: ''
  });

  const loadApplicants = useCallback(async () => {
    // Don't show loading spinner on search/filter changes, only initial load
    if (applicantsList.length === 0) {
      setLoading(true);
    }
    try {
      const params = {
        status: 'active',
        page: currentPage,
        limit: itemsPerPage,
        sort_by: sortField,
        sort_order: sortDirection,
        search: activeSearch,
        position_filter: filters.position,
        location_filter: filters.location,
        viewed: filters.viewed,
        interviewed: filters.interviewed,
        min_rating: filters.minRating,
        max_rating: filters.maxRating
      };
      
      const response = await applicants.getAll(params);
      setApplicantsList(response.data.data);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error loading applicants:', error);
    } finally {
      setLoading(false);
    }
  }, [activeSearch, filters, sortField, sortDirection, currentPage, itemsPerPage, applicantsList.length]);

  // Load applicants when dependencies change
  useEffect(() => {
    loadApplicants();
  }, [loadApplicants]);

  // Filter dropdown options come from the full data set, not just the current
  // page, so a position that happens to be on page 3 is still selectable.
  useEffect(() => {
    applicants.getFilterOptions('active')
      .then((response) => setFilterOptions(response.data))
      .catch(() => setFilterOptions({ positions: [], locations: [] }));
  }, []);

  const handleSearch = () => {
    setActiveSearch(searchText);
    setCurrentPage(1); // Reset to page 1 when searching
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleMove = async (id, status) => {
    try {
      await applicants.move(id, status);
      loadApplicants();
    } catch (error) {
      alert('Error moving applicant');
    }
  };

  const handleUpdate = async (id, field, value) => {
    try {
      await applicants.update(id, { [field]: value });
      loadApplicants();
    } catch (error) {
      alert('Error updating applicant');
    }
  };

  const handleViewDetails = (applicant) => {
    setViewingApplicant(applicant);
    setContactInfo({
      first_name: applicant.first_name || '',
      last_name: applicant.last_name || '',
      email: applicant.email || '',
      phone: applicant.phone || '',
      // There is no phone_country column on the applicants table, so the
      // country is recovered from the stored value itself (see
      // guessPhoneCountry in utils/phone.js). Numbers saved since this
      // feature shipped carry a "+1" etc. and resolve correctly; older
      // numbers with no "+" fall back to the default and the admin can
      // correct the dropdown if that guess is wrong.
      phone_country: guessPhoneCountry(applicant.phone),
      address: applicant.address || '',
      city: applicant.city || '',
      state: applicant.state || '',
      zip: applicant.zip || ''
    });
    setContactPhoneError('');
    setEditingContact(false);
    setShowDetailsModal(true);
  };

  const handleSaveContactInfo = async () => {
    // Mirrors the validation on the public application form. Previously this
    // endpoint accepted any string at all for phone, which could silently
    // reintroduce unformatted or invalid numbers after the public form was
    // fixed to reject them.
    if (contactInfo.phone && !isValidPhoneForCountry(contactInfo.phone, contactInfo.phone_country)) {
      setContactPhoneError('Please enter a valid phone number for the selected country.');
      return;
    }
    setContactPhoneError('');

    try {
      await applicants.update(viewingApplicant.id, contactInfo);
      setEditingContact(false);
      // Update local state
      setViewingApplicant({...viewingApplicant, ...contactInfo});
      loadApplicants();
    } catch (error) {
      alert(error.response?.data?.error || 'Error updating contact information');
    }
  };


  const handleExportPDF = async () => {
    try {
      const response = await applicants.download(viewingApplicant.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `application_${viewingApplicant.first_name}_${viewingApplicant.last_name}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Error downloading PDF');
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return ' ⇅';
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  const clearFilters = () => {
    setSearchText('');
    setActiveSearch(''); // Clear the active search to reset the table
    setFilters({
      position: '',
      location: '',
      viewed: '',
      interviewed: '',
      minRating: '',
      maxRating: ''
    });
  };

  const handleOpenAdvancedFilter = () => {
    setTempFilters({...filters});
    setShowAdvancedFilter(true);
  };

  const handleApplyFilters = () => {
    setFilters({...tempFilters});
    setShowAdvancedFilter(false);
  };

  const handleCancelFilters = () => {
    setShowAdvancedFilter(false);
  };

  const uniquePositions = filterOptions.positions;
  const uniqueLocations = filterOptions.locations;

  // Calculate display values from server pagination
  const startIndex = (pagination.page - 1) * pagination.limit;
  const endIndex = Math.min(startIndex + pagination.limit, pagination.total);

  if (loading) return <div className="spinner"></div>;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Active List</h1>
        <p className="page-subtitle">Applicants pending review</p>
      </div>

      {/* Search and Filter Bar */}
      <div className="card" style={{marginBottom: '20px'}}>
        <div style={{display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap'}}>
          <div style={{display: 'flex', gap: '8px', flex: '1', minWidth: '250px'}}>
            <input
              type="text"
              className="form-input"
              placeholder="Search by name..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              style={{flex: '1'}}
            />
            <button 
              className="btn btn-primary" 
              onClick={handleSearch}
            >
              🔍 Search
            </button>
          </div>
          <button 
            className="btn btn-outline" 
            onClick={handleOpenAdvancedFilter}
          >
            🔧 Advanced Filter
          </button>
          {(activeSearch || filters.position || filters.location || filters.viewed !== '' || filters.interviewed !== '' || filters.minRating || filters.maxRating) && (
            <button 
              className="btn btn-outline" 
              onClick={clearFilters}
              style={{borderColor: '#dc2626', color: '#dc2626'}}
            >
              ✕ Clear Filters
            </button>
          )}
          <div style={{marginLeft: 'auto', color: '#64748b', fontSize: '14px'}}>
            Showing {startIndex + 1}-{endIndex} of {pagination.total}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th onClick={() => handleSort('first_name')} style={{cursor: 'pointer', userSelect: 'none'}}>
                  Name{getSortIcon('first_name')}
                </th>
                <th onClick={() => handleSort('phone')} style={{cursor: 'pointer', userSelect: 'none'}}>
                  Phone{getSortIcon('phone')}
                </th>
                <th onClick={() => handleSort('position_title')} style={{cursor: 'pointer', userSelect: 'none'}}>
                  Position{getSortIcon('position_title')}
                </th>
                <th onClick={() => handleSort('position_location')} style={{cursor: 'pointer', userSelect: 'none'}}>
                  Location{getSortIcon('position_location')}
                </th>
                <th onClick={() => handleSort('applied_date')} style={{cursor: 'pointer', userSelect: 'none'}}>
                  Applied Date{getSortIcon('applied_date')}
                </th>
                <th onClick={() => handleSort('viewed')} style={{cursor: 'pointer', userSelect: 'none'}}>
                  Viewed{getSortIcon('viewed')}
                </th>
                <th onClick={() => handleSort('interviewed')} style={{cursor: 'pointer', userSelect: 'none'}}>
                  Interviewed{getSortIcon('interviewed')}
                </th>
                <th onClick={() => handleSort('rating')} style={{cursor: 'pointer', userSelect: 'none'}}>
                  Rating{getSortIcon('rating')}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {applicantsList.map(applicant => (
                <tr key={applicant.id} style={{cursor: 'pointer'}}>
                  <td onClick={() => handleViewDetails(applicant)}><strong>{applicant.first_name} {applicant.last_name}</strong></td>
                  <td onClick={() => handleViewDetails(applicant)}>{applicant.phone || 'N/A'}</td>
                  <td onClick={() => handleViewDetails(applicant)}>{applicant.position_title}</td>
                  <td onClick={() => handleViewDetails(applicant)}>{applicant.position_location || 'N/A'}</td>
                  <td onClick={() => handleViewDetails(applicant)}>{new Date(applicant.applied_date).toLocaleDateString()}</td>
                  <td>
                    <input
                      type="checkbox"
                      checked={applicant.viewed}
                      onChange={(e) => handleUpdate(applicant.id, 'viewed', e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={applicant.interviewed}
                      onChange={(e) => handleUpdate(applicant.id, 'interviewed', e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="1"
                      max="5"
                      value={applicant.rating || ''}
                      onChange={(e) => handleUpdate(applicant.id, 'rating', e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ width: '60px' }}
                      className="form-input"
                    />
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {user?.permissions?.edit_applicants && (
                      <div className="flex gap-1">
                        <button
                          className="btn btn-sm btn-success"
                          onClick={() => handleMove(applicant.id, 'hired')}
                        >
                          Hire
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleMove(applicant.id, 'released')}
                        >
                          Release
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {pagination.totalPages > 1 && (
          <div style={{
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            gap: '8px', 
            padding: '20px',
            borderTop: '1px solid #e2e8f0'
          }}>
            <button
              className="btn btn-sm btn-outline"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              style={{opacity: currentPage === 1 ? 0.5 : 1}}
            >
              ⟪ First
            </button>
            <button
              className="btn btn-sm btn-outline"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              style={{opacity: currentPage === 1 ? 0.5 : 1}}
            >
              ← Prev
            </button>
            
            <div style={{display: 'flex', gap: '4px', alignItems: 'center'}}>
              {[...Array(pagination.totalPages)].map((_, idx) => {
                const pageNum = idx + 1;
                // Show first 2, last 2, and pages around current
                if (
                  pageNum === 1 || 
                  pageNum === 2 ||
                  pageNum === pagination.totalPages - 1 ||
                  pageNum === pagination.totalPages ||
                  (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={pageNum}
                      className={currentPage === pageNum ? "btn btn-sm btn-primary" : "btn btn-sm btn-outline"}
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                } else if (
                  pageNum === currentPage - 2 ||
                  pageNum === currentPage + 2
                ) {
                  return <span key={pageNum}>...</span>;
                }
                return null;
              })}
            </div>

            <button
              className="btn btn-sm btn-outline"
              onClick={() => setCurrentPage(prev => Math.min(pagination.totalPages, prev + 1))}
              disabled={currentPage === pagination.totalPages}
              style={{opacity: currentPage === pagination.totalPages ? 0.5 : 1}}
            >
              Next →
            </button>
            <button
              className="btn btn-sm btn-outline"
              onClick={() => setCurrentPage(pagination.totalPages)}
              disabled={currentPage === pagination.totalPages}
              style={{opacity: currentPage === pagination.totalPages ? 0.5 : 1}}
            >
              Last ⟫
            </button>
          </div>
        )}
      </div>

      {/* Advanced Filter Modal */}
      {showAdvancedFilter && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth: '600px'}}>
            <div className="modal-header">
              <h3 className="modal-title">Advanced Filter</h3>
              <button onClick={handleCancelFilters}>×</button>
            </div>
            <div className="modal-body">
              <div className="grid grid-2" style={{gap: '16px'}}>
                <div className="form-group">
                  <label className="form-label">Position</label>
                  <select
                    className="form-select"
                    value={tempFilters.position}
                    onChange={(e) => setTempFilters({...tempFilters, position: e.target.value})}
                  >
                    <option value="">All Positions</option>
                    {uniquePositions.map(pos => (
                      <option key={pos} value={pos}>{pos}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Location</label>
                  <select
                    className="form-select"
                    value={tempFilters.location}
                    onChange={(e) => setTempFilters({...tempFilters, location: e.target.value})}
                  >
                    <option value="">All Locations</option>
                    {uniqueLocations.map(loc => (
                      <option key={loc} value={loc}>{loc}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Viewed Status</label>
                  <select
                    className="form-select"
                    value={tempFilters.viewed}
                    onChange={(e) => setTempFilters({...tempFilters, viewed: e.target.value})}
                  >
                    <option value="">All</option>
                    <option value="true">Viewed</option>
                    <option value="false">Not Viewed</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Interviewed Status</label>
                  <select
                    className="form-select"
                    value={tempFilters.interviewed}
                    onChange={(e) => setTempFilters({...tempFilters, interviewed: e.target.value})}
                  >
                    <option value="">All</option>
                    <option value="true">Interviewed</option>
                    <option value="false">Not Interviewed</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Min Rating</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder="e.g., 1"
                    min="1"
                    max="5"
                    value={tempFilters.minRating}
                    onChange={(e) => setTempFilters({...tempFilters, minRating: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Max Rating</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder="e.g., 5"
                    min="1"
                    max="5"
                    value={tempFilters.maxRating}
                    onChange={(e) => setTempFilters({...tempFilters, maxRating: e.target.value})}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-outline" 
                onClick={handleCancelFilters}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleApplyFilters}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Applicant Details Modal */}
      {showDetailsModal && viewingApplicant && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth: '900px'}}>
            <div className="modal-header">
              <h3 className="modal-title">Application Details</h3>
              <button onClick={() => setShowDetailsModal(false)}>×</button>
            </div>
            <div className="modal-body" style={{maxHeight: '70vh', overflowY: 'auto'}}>
              {/* Header Section */}
              <div style={{marginBottom: '24px', paddingBottom: '16px', borderBottom: '2px solid #e2e8f0'}}>
                <h2 style={{marginBottom: '8px'}}>{viewingApplicant.first_name} {viewingApplicant.last_name}</h2>
                <div style={{display: 'flex', gap: '12px', flexWrap: 'wrap'}}>
                  <span className="badge badge-primary">{viewingApplicant.position_title}</span>
                  <span className="badge badge-secondary">{viewingApplicant.position_location}</span>
                  <span className="badge badge-info">Applied: {new Date(viewingApplicant.applied_date).toLocaleDateString()}</span>
                  {viewingApplicant.rating && <span className="badge badge-success">Rating: {viewingApplicant.rating}/5</span>}
                </div>
              </div>

              {/* Contact Information */}
              <div className="review-section">
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px'}}>
                  <h3 style={{margin: 0}}>Contact Information</h3>
                  {user?.permissions?.edit_applicants && !editingContact && (
                    <button 
                      className="btn btn-sm btn-primary"
                      onClick={() => setEditingContact(true)}
                    >
                      ✏️ Edit Contact
                    </button>
                  )}
                </div>
                
                {editingContact ? (
                  <div>
                    <div className="grid grid-2" style={{gap: '12px'}}>
                      <div className="form-group">
                        <label className="form-label">First Name *</label>
                        <input
                          type="text"
                          className="form-input"
                          value={contactInfo.first_name}
                          onChange={(e) => setContactInfo({...contactInfo, first_name: e.target.value})}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Last Name *</label>
                        <input
                          type="text"
                          className="form-input"
                          value={contactInfo.last_name}
                          onChange={(e) => setContactInfo({...contactInfo, last_name: e.target.value})}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Email *</label>
                        <input
                          type="email"
                          className="form-input"
                          value={contactInfo.email}
                          onChange={(e) => setContactInfo({...contactInfo, email: e.target.value})}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Phone</label>
                        <div className="phone-input-row">
                          <select
                            className="form-select phone-country-select"
                            value={contactInfo.phone_country}
                            onChange={(e) => setContactInfo({...contactInfo, phone_country: e.target.value})}
                            aria-label="Phone country"
                          >
                            {COUNTRY_OPTIONS.map(({ code, name }) => (
                              <option key={code} value={code}>{name}</option>
                            ))}
                          </select>
                          <input
                            type="tel"
                            className="form-input"
                            value={contactInfo.phone}
                            onChange={(e) => setContactInfo({...contactInfo, phone: e.target.value})}
                          />
                        </div>
                        {contactPhoneError && (
                          <div style={{color: '#b91c1c', marginTop: '6px', fontSize: '13px'}}>{contactPhoneError}</div>
                        )}
                      </div>

                      <div className="form-group" style={{gridColumn: '1 / -1'}}>
                        <label className="form-label">Address</label>
                        <input
                          type="text"
                          className="form-input"
                          value={contactInfo.address}
                          onChange={(e) => setContactInfo({...contactInfo, address: e.target.value})}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">City</label>
                        <input
                          type="text"
                          className="form-input"
                          value={contactInfo.city}
                          onChange={(e) => setContactInfo({...contactInfo, city: e.target.value})}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">State</label>
                        <input
                          type="text"
                          className="form-input"
                          value={contactInfo.state}
                          onChange={(e) => setContactInfo({...contactInfo, state: e.target.value})}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">ZIP</label>
                        <input
                          type="text"
                          className="form-input"
                          value={contactInfo.zip}
                          onChange={(e) => setContactInfo({...contactInfo, zip: e.target.value})}
                        />
                      </div>
                    </div>
                    <div style={{display: 'flex', gap: '8px', marginTop: '12px'}}>
                      <button className="btn btn-primary" onClick={handleSaveContactInfo}>
                        Save Changes
                      </button>
                      <button 
                        className="btn btn-outline" 
                        onClick={() => {
                          setEditingContact(false);
                          setContactPhoneError('');
                          setContactInfo({
                            first_name: viewingApplicant.first_name || '',
                            last_name: viewingApplicant.last_name || '',
                            email: viewingApplicant.email || '',
                            phone: viewingApplicant.phone || '',
                            phone_country: guessPhoneCountry(viewingApplicant.phone),
                            address: viewingApplicant.address || '',
                            city: viewingApplicant.city || '',
                            state: viewingApplicant.state || '',
                            zip: viewingApplicant.zip || ''
                          });
                        }}

                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-2" style={{gap: '12px'}}>
                    <div className="review-item"><strong>Email:</strong> {viewingApplicant.email}</div>
                    <div className="review-item"><strong>Phone:</strong> {viewingApplicant.phone || 'N/A'}</div>
                    <div className="review-item"><strong>Address:</strong> {viewingApplicant.address || 'N/A'}</div>
                    <div className="review-item"><strong>City, State ZIP:</strong> {viewingApplicant.city}, {viewingApplicant.state} {viewingApplicant.zip}</div>
                  </div>
                )}
              </div>

              {/* General Questions */}
              <div className="review-section">
                <h3>General Questions</h3>
                <div className="grid grid-2" style={{gap: '12px'}}>
                  <div className="review-item"><strong>Worked here before:</strong> {viewingApplicant.worked_before || 'N/A'}</div>
                  <div className="review-item"><strong>Years of experience:</strong> {viewingApplicant.years_experience || 'N/A'}</div>
                  <div className="review-item"><strong>At least 18:</strong> {viewingApplicant.at_least_18 || 'N/A'}</div>
                  <div className="review-item"><strong>FL License/ID:</strong> {viewingApplicant.has_fl_license || 'N/A'}</div>
                  <div className="review-item"><strong>Felony conviction:</strong> {viewingApplicant.felony_conviction || 'N/A'}</div>
                  <div className="review-item"><strong>Military service:</strong> {viewingApplicant.military_service || 'N/A'}</div>
                  <div className="review-item"><strong>Education level:</strong> {viewingApplicant.education_level || 'N/A'}</div>
                </div>
              </div>

              {/* Schedule Preferences */}
              <div className="review-section">
                <h3>Schedule Preferences</h3>
                <div className="grid grid-2" style={{gap: '12px'}}>
                  <div className="review-item"><strong>Employment type:</strong> {viewingApplicant.employment_type || 'N/A'}</div>
                  <div className="review-item"><strong>Shift preference:</strong> {viewingApplicant.shift_preference || 'N/A'}</div>
                  <div className="review-item"><strong>Changing schedule:</strong> {viewingApplicant.changing_schedule || 'N/A'}</div>
                  <div className="review-item"><strong>Start date:</strong> {viewingApplicant.start_date || 'N/A'}</div>
                  {viewingApplicant.available_days && (
                    <div className="review-item" style={{gridColumn: '1 / -1'}}>
                      <strong>Available days:</strong> {JSON.parse(viewingApplicant.available_days).join(', ')}
                    </div>
                  )}
                </div>
              </div>

              {/* References */}
              {viewingApplicant.has_references === 'Yes' && viewingApplicant.references_data && (
                <div className="review-section">
                  <h3>References</h3>
                  {JSON.parse(viewingApplicant.references_data).map((ref, index) => (
                    <div key={index} className="review-item">
                      <strong>Reference {index + 1}:</strong> {ref.name} ({ref.phone})
                      {ref.relationship && ` - ${ref.relationship}`}
                    </div>
                  ))}
                </div>
              )}

              {/* Status Information */}
              <div className="review-section">
                <h3>Status Information</h3>
                <div className="grid grid-2" style={{gap: '12px'}}>
                  <div className="review-item"><strong>Viewed:</strong> {viewingApplicant.viewed ? 'Yes' : 'No'}</div>
                  <div className="review-item"><strong>Interviewed:</strong> {viewingApplicant.interviewed ? 'Yes' : 'No'}</div>
                  <div className="review-item"><strong>Rating:</strong> {viewingApplicant.rating || 'Not rated'}</div>
                </div>
              </div>

              {/* Resume / CV */}
              <ApplicantResume
                applicantId={viewingApplicant.id}
                applicantName={viewingApplicant.first_name}
              />

              {/* Notes Section */}
              <ApplicantNotes
                applicantId={viewingApplicant.id}
                canEdit={user?.role === 'admin' || user?.permissions?.edit_applicants}
              />
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowDetailsModal(false)}>
                Close
              </button>
              <button className="btn btn-primary" onClick={handleExportPDF}>
                📄 Export to PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ActiveList;
